import React, { useEffect, useState, useRef } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import useCart from "../hooks/useCart";
import useAuthRole from "../hooks/useAuthRole";
import { auth } from "../services/firebase";
import { signOut } from "firebase/auth";

export default function Header() {
  const { itemCount } = useCart();
  const { user, roles, loading } = useAuthRole();
  const canCashier = roles.cashier || roles.manager || roles.admin;
  const canManager = roles.manager || roles.admin;
  const isAdmin = !!roles.admin;

  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const searchRefDesktop = useRef(null);
  const searchRefMobile = useRef(null);
  const panelRef = useRef(null);

  // Ferme le menu au changement de page
  useEffect(() => setOpen(false), [location.pathname]);

  // Ferme avec la touche Échap
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  // Ferme en cliquant à l’extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSearch = (e, fromMobile = false) => {
    e.preventDefault();
    const inputRef = fromMobile ? searchRefMobile : searchRefDesktop;
    const q = inputRef.current?.value?.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const isLogged = !!user;

  return (
    <header className="wc-navbar" role="banner" aria-label="Navigation principale">
      {/* Logo */}
      <Link to="/" className="wc-brand" aria-label="Accueil - Wine Cella">
        
        <span>Ets EKLU</span>
      </Link>

      <nav className="wc-nav" role="navigation" aria-label="Menu principal">
        {/* Liens desktop */}
        <div className="wc-nav-links">
          {/* === VISITEUR (non connecté) : MENUS PUBLICS === */}
          {!isLogged && (
            <>
              <NavLink to="/" end>Nos produits</NavLink>
              <NavLink to="/orders">Commandes</NavLink>
              <NavLink to="/cart" className="btn-cart">
                Panier {itemCount ? <span className="badge ms-1">{itemCount}</span> : ""}
              </NavLink>
              <NavLink to="/checkout" className="btn-cta">Commander</NavLink>
            </>
          )}

          {/* === CONNECTÉ : UNIQUEMENT POS / INVENTAIRE / ADMIN === */}
          {isLogged && (
            <>
              {canCashier && (
                <>
                  <NavLink to="/pos">POS</NavLink>
                  <NavLink to="/cash">Caisse</NavLink>
                  <NavLink to="/receipts">Réceptions</NavLink>
                </>
              )}
              {canManager && (
                <>
                  <NavLink to="/stock">Stock</NavLink>
                  <NavLink to="/purchases">Achats</NavLink>
                  <NavLink to="/admin/inventory">Inventaire</NavLink>
                  <NavLink to="/admin/purchase-orders">PO</NavLink>
                </>
              )}
              {(canManager || isAdmin) && <NavLink to="/admin">Admin</NavLink>}
              {isAdmin && <NavLink to="/admin/roles">Rôles</NavLink>}
            </>
          )}
        </div>

        {/* Recherche desktop — visible uniquement pour visiteurs */}
        {!isLogged && (
          <form
            onSubmit={(e) => handleSearch(e, false)}
            className="wc-search wc-search-desktop ms-3"
            role="search"
          >
            <input
              ref={searchRefDesktop}
              className="form-control"
              type="search"
              placeholder="Recherche..."
              aria-label="Recherche"
            />
          </form>
        )}

        {/* Auth à droite (desktop) */}
        <div className="ms-3 d-none d-md-flex align-items-center gap-2">
          {!loading && user ? (
            <>
              <span className="small text-muted">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", marginRight: 6 }}
                  />
                ) : null}
                {user.displayName || user.email}
              </span>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => signOut(auth)}
              >
                Déconnexion
              </button>
            </>
          ) : (
            <NavLink to="/login" className="btn btn-dark btn-sm">Connexion</NavLink>
          )}
        </div>

        {/* Bouton mobile */}
        <button
          type="button"
          className="wc-nav-toggle btn btn-outline-light ms-2 d-md-none"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls="mobile-nav-panel"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
        >
          ☰
        </button>

        {/* Panneau mobile */}
        <div
          id="mobile-nav-panel"
          ref={panelRef}
          className={`wc-nav-panel ${open ? "open" : ""}`}
          aria-hidden={!open}
        >
          {/* VISITEUR : menus publics + recherche */}
          {!isLogged && (
            <>
              <NavLink to="/" end>Nos produits</NavLink>
              <NavLink to="/orders">Commandes</NavLink>
              <NavLink to="/cart">Panier {itemCount ? `(${itemCount})` : ""}</NavLink>
              <NavLink to="/checkout">Commander</NavLink>

              <form onSubmit={(e) => handleSearch(e, true)} className="mt-3 wc-search wc-search-mobile">
                <input
                  ref={searchRefMobile}
                  type="search"
                  className="form-control"
                  placeholder="Rechercher un vin..."
                  aria-label="Recherche"
                />
              </form>
            </>
          )}

          {/* CONNECTÉ : uniquement POS/Inventaire/Admin */}
          {isLogged && (
            <>
              {canCashier && (
                <>
                  <NavLink to="/pos">POS</NavLink>
                  <NavLink to="/cash">Caisse</NavLink>
                  <NavLink to="/receipts">Réceptions</NavLink>
                </>
              )}
              {canManager && (
                <>
                  <NavLink to="/stock">Stock</NavLink>
                  <NavLink to="/purchases">Achats</NavLink>
                  <NavLink to="/admin/inventory">Inventaire</NavLink>
                  <NavLink to="/admin/purchase-orders">PO</NavLink>
                </>
              )}
              {(canManager || isAdmin) && <NavLink to="/admin">Admin</NavLink>}
              {isAdmin && <NavLink to="/admin/roles">Rôles</NavLink>}
            </>
          )}

          {/* Auth mobile */}
          <div className="mt-3">
            {!loading && user ? (
              <button
                type="button"
                className="btn btn-outline-danger w-100"
                onClick={() => { signOut(auth); setOpen(false); }}
              >
                Déconnexion
              </button>
            ) : (
              <NavLink to="/login" className="btn btn-dark w-100" onClick={() => setOpen(false)}>
                Connexion
              </NavLink>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}


